import React from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import { Bold, Italic, List, ListOrdered } from "lucide-react";

const ToolbarBtn = ({ onClick, active, children }) => (
  <button
    type="button"
    onMouseDown={(e) => { e.preventDefault(); onClick(); }}
    className="p-1.5 rounded transition-colors hover:bg-stone-100"
    style={{ color: active ? "#292524" : "#A8A29E", background: active ? "#F5F3EF" : "transparent" }}
  >
    {children}
  </button>
);

export default function RichTextEditor({ value, onChange, placeholder = "Write something...", minHeight = 120 }) {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Placeholder.configure({ placeholder }),
    ],
    content: value || "",
    onUpdate({ editor }) {
      const html = editor.getHTML();
      onChange(html === "<p></p>" ? "" : html);
    },
    editorProps: {
      attributes: {
        class: "outline-none",
        style: `min-height:${minHeight}px; padding: 12px; font-size: 14px; color: #292524; line-height: 1.6;`,
      },
    },
  });

  // Sync external value changes (e.g. when loading data)
  React.useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const incoming = value || "";
    if (current !== incoming && !editor.isFocused) {
      editor.commands.setContent(incoming, false);
    }
  }, [value, editor]);

  if (!editor) return null;

  return (
    <div className="rounded-lg border overflow-hidden" style={{ borderColor: "#E7E5E4" }}>
      {/* Toolbar */}
      <div className="flex items-center gap-0.5 px-2 py-1.5 border-b" style={{ borderColor: "#E7E5E4", background: "#FAFAF9" }}>
        <ToolbarBtn onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive("bold")}>
          <Bold size={13} strokeWidth={2} />
        </ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive("italic")}>
          <Italic size={13} strokeWidth={2} />
        </ToolbarBtn>
        <div className="w-px h-4 mx-1" style={{ background: "#E7E5E4" }} />
        <ToolbarBtn onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive("bulletList")}>
          <List size={13} strokeWidth={2} />
        </ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive("orderedList")}>
          <ListOrdered size={13} strokeWidth={2} />
        </ToolbarBtn>
      </div>
      {/* Editor */}
      <div style={{ background: "#fff" }}>
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}

/** Render stored HTML safely in read-only contexts */
export function RichTextDisplay({ html, className = "" }) {
  if (!html) return null;
  // Plain text fallback for old records
  const isHtml = html.startsWith("<");
  if (!isHtml) return <p className={`text-sm leading-relaxed ${className}`} style={{ color: "#57534E" }}>{html}</p>;
  return (
    <div
      className={`prose-sm text-sm leading-relaxed ${className}`}
      style={{ color: "#57534E" }}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
